import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Shield, UserCog } from 'lucide-react';
import { auth } from '../utils/auth';
import { useLanguage } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role: 'editor' });

  const fetchUsers = useCallback(async () => {
    const token = auth.getToken();
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('toastNoPermission'));
        navigate('/admin/dashboard');
      } else {
        toast.error(t('toastUsersLoadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [t, navigate]);

  useEffect(() => {
    const token = auth.getToken();
    const adminUser = auth.getUser() || {};
    if (!token || adminUser.role !== 'admin') {
      toast.error(t('toastNoPermission'));
      navigate('/admin/dashboard');
      return;
    }
    fetchUsers();
  }, [fetchUsers, t, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = auth.getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingUser) {
        const updateData = {};
        if (formData.username) updateData.username = formData.username;
        if (formData.email) updateData.email = formData.email;
        if (formData.password) updateData.password = formData.password;
        if (formData.role) updateData.role = formData.role;
        await axios.patch(`${API}/users/${editingUser.id}`, updateData, { headers });
        toast.success(t('toastUserUpdated'));
      } else {
        if (!formData.password) {
          toast.error(t('toastPasswordRequired'));
          return;
        }
        await axios.post(`${API}/users`, formData, { headers });
        toast.success(t('toastUserCreated'));
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'editor' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toastSaveError'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, email: user.email || '', password: '', role: user.role });
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Benutzer "${user.username}" wirklich löschen?`)) return;
    const token = auth.getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/users/${user.id}`, { headers });
      toast.success(t('toastUserDeleted'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toastDeleteError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  };

  return (
    <div className="admin-container" data-testid="admin-users-page">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/admin/dashboard">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ArrowLeft size={18} /> Zurück
            </button>
          </Link>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20' }}>Benutzerverwaltung</h1>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingUser(null); setFormData({ username: '', email: '', password: '', role: 'editor' }); }}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          data-testid="add-user-btn"
        >
          <Plus size={18} /> Neuer Benutzer
        </button>
      </div>

      <div className="admin-content">
        {/* Create/Edit Form */}
        {showForm && (
          <div className="admin-card" style={{ marginBottom: '1.5rem' }} data-testid="user-form">
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              {editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Benutzername *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="form-input"
                    required
                    data-testid="user-form-username"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">E-Mail *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input"
                    required
                    data-testid="user-form-email"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Passwort {editingUser ? '(leer = unverändert)' : '*'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="form-input"
                    required={!editingUser}
                    data-testid="user-form-password"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Rolle *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="form-select"
                    data-testid="user-form-role"
                  >
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="submit" className="btn-primary" data-testid="user-form-submit">
                  {editingUser ? 'Speichern' : 'Erstellen'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditingUser(null); }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div className="admin-card">
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>Benutzer ({users.length})</h2>
          {loading ? (
            <p>Laden...</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="users-table">
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Benutzername</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>E-Mail</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Rolle</th>
                    <th style={{ padding: '0.75rem 0.5rem' }}>Erstellt</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }} data-testid={`user-row-${user.username}`}>
                      <td style={{ padding: '0.75rem 0.5rem', fontWeight: '500' }}>{user.username}</td>
                      <td style={{ padding: '0.75rem 0.5rem', color: '#666' }}>{user.email || '-'}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                          padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600',
                          background: user.role === 'admin' ? '#E8F5E9' : '#E3F2FD',
                          color: user.role === 'admin' ? '#1B5E20' : '#0D47A1'
                        }}>
                          {user.role === 'admin' ? <Shield size={14} /> : <UserCog size={14} />}
                          {user.role === 'admin' ? 'Admin' : 'Editor'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', color: '#666' }}>{formatDate(user.created_at)}</td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleEdit(user)}
                            className="btn-secondary"
                            style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                            data-testid={`edit-user-${user.username}`}
                          >
                            <Pencil size={14} /> Bearbeiten
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            style={{
                              padding: '0.35rem 0.75rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem',
                              background: '#FFEBEE', color: '#C62828', border: '1px solid #EF9A9A', borderRadius: '6px', cursor: 'pointer'
                            }}
                            data-testid={`delete-user-${user.username}`}
                          >
                            <Trash2 size={14} /> Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
